import { IAgentBlueprint } from "../types/base";
import {
  IPosProviderClover,
  IPosProviderFoodtec,
  IPosProviderKwick,
  IPosProviderLoman,
  IPosProviderSpotOn,
  IPosProviderSquare,
  IPosProviderStream,
  IPosProviderToast,
} from "../types/pos";
import {
  BaseAgentFormValues,
  CreateAgentFormValues,
  MenuConfigFormValues,
  PatchAgentFormValues,
} from "@/types/forms";
import { IMenuModuleConfig, Menu } from "@/types/menu";

/**
 * Base function that converts IAgentBlueprint to common form values
 */
const convertAgentBlueprintToBaseFormValues = (
  agent: IAgentBlueprint,
): BaseAgentFormValues => {
  const result: BaseAgentFormValues = {
    id: agent.id,
    name: agent.name,
    type: agent.type,
    placeId: agent.placeId,
    timezone: agent.timezone,
    hours: agent.hours,
    hoursOrdering: agent.hoursOrdering ?? null,
    accountId: agent.accountId,

    twilioOverride: agent.twilioOverride
      ? {
          enabled: agent.twilioOverride.enabled || false,
          sid: agent.twilioOverride.sid,
          auth: agent.twilioOverride.auth,
        }
      : undefined,
    smsPhoneNumber: agent.smsPhoneNumber,

    greetingMessageOpen: agent.greetingMessageOpen,
    greetingMessageOrderingClosed: agent.greetingMessageOrderingClosed,
    greetingMessageClosed: agent.greetingMessageClosed,
    prompt: agent.prompt,

    fallbackPhoneNumber: agent.fallbackPhoneNumber,
    transfers: agent.transfers,
    messages: agent.messages,

    twoPartyConsent: agent.twoPartyConsent,

    streamConnectedAt: agent.streamConnectedAt,

    pos: agent.pos,
    // Root-level prep times
    pickupPrepTime: agent.pickupPrepTime
      ? parseInt(agent.pickupPrepTime)
      : null,
    deliveryPrepTime: agent.deliveryPrepTime
      ? parseInt(agent.deliveryPrepTime)
      : null,
    customPrepTimeEnabled: agent.customPrepTimeEnabled,
    customPrepTimeHours: agent.customPrepTimeHours,
  };

  // pos specific fields
  if (agent.pos === "toast") {
    const toastProvider = agent.posProvider as IPosProviderToast;
    result.toastUrl = toastProvider.url;
  } else if (agent.pos === "square") {
    const squareProvider = agent.posProvider as IPosProviderSquare;
    result.squareMerchantId = squareProvider.merchantId;
    result.squareLocationId = squareProvider.locationId;
    result.squareAccessToken = squareProvider.accessToken;
    result.squareRefreshToken = squareProvider.refreshToken;
  } else if (agent.pos === "clover") {
    const cloverProvider = agent.posProvider as IPosProviderClover;
    result.cloverMerchantId = cloverProvider.merchantId;
    result.cloverAccessToken = cloverProvider.accessToken;
    result.cloverRefreshToken = cloverProvider.refreshToken;
    result.cloverRefreshTokenExpiration = cloverProvider.refreshTokenExpiration;
    result.cloverOrderTypeId = cloverProvider.orderTypeId;
  } else if (agent.pos === "kwick") {
    const kwickProvider = agent.posProvider as IPosProviderKwick;
    result.kwickMerchantKey = kwickProvider.merchantKey;
    result.kwickMerchantToken = kwickProvider.merchantToken;
  } else if (agent.pos === "stream") {
    const streamProvider = agent.posProvider as IPosProviderStream;
    result.subprovider = streamProvider.subprovider;
    result.locationId = streamProvider.locationId;
  } else if (agent.pos === "spotOn") {
    const spotOnProvider = agent.posProvider as IPosProviderSpotOn;
    result.spotOnLocationId = spotOnProvider.locationId;
  } else if (agent.pos === "foodtec") {
    const foodtecProvider = agent.posProvider as IPosProviderFoodtec;
    result.foodtecBaseUrl = foodtecProvider.baseUrl;
    result.foodtecMenuAuth = foodtecProvider.menuAuth;
    result.foodtecCheckoutAuth = foodtecProvider.checkoutAuth;
    result.foodtecOrderAuth = foodtecProvider.orderAuth;
    result.foodtecOrderTypes = foodtecProvider.orderTypes;
  }

  // menu config
  if (agent.posProvider?.menuModuleConfig) {
    result.menuModuleConfig = convertMenuConfigToFormValues(
      agent.posProvider.menuModuleConfig,
    );
  }

  // ordering config
  if (agent.posProvider?.orderingModuleConfig) {
    const orderingModuleConfig = agent.posProvider.orderingModuleConfig;
    result.orderingOrderUntilClosed = orderingModuleConfig.orderUntilClosed;
    result.orderingAllowScheduledOrders =
      orderingModuleConfig.allowScheduledOrders;
    result.orderingShouldTotal = orderingModuleConfig.shouldTotal;
    result.orderingExactTimeEstimate = orderingModuleConfig.exactTimeEstimate;
    result.orderingInstantSubmit = orderingModuleConfig.instantSubmit;
    result.orderingMinLeadTime = orderingModuleConfig.minLeadTime;
    result.orderingMinTotalThreshold = orderingModuleConfig.minTotalThreshold;
    result.orderingMaxTotalThreshold = orderingModuleConfig.maxTotalThreshold;
    if (agent.posProvider.provider === "toast") {
      const toastOrderingModuleConfig = (agent.posProvider as IPosProviderToast)
        .orderingModuleConfig;
      result.orderingDynamicPrepTime =
        toastOrderingModuleConfig.dynamicPrepTime;
      result.orderingTaxRate = toastOrderingModuleConfig.taxRate;
    } else if (agent.posProvider.provider === "loman") {
      const lomanOrderingModuleConfig = (agent.posProvider as IPosProviderLoman)
        .orderingModuleConfig;
      result.orderingTaxRate = lomanOrderingModuleConfig.taxRate;
    } else if (agent.posProvider.provider === "stream") {
      const streamOrderingModuleConfig = (
        agent.posProvider as IPosProviderStream
      ).orderingModuleConfig;
      result.useOrderEntryTool = streamOrderingModuleConfig.useOrderEntryTool;
    } else if (agent.posProvider.provider === "foodtec") {
      const foodtecOrderingModuleConfig = (
        agent.posProvider as IPosProviderFoodtec
      ).orderingModuleConfig;
      result.useOrderEntryTool = foodtecOrderingModuleConfig.useOrderEntryTool;
    }
  }

  // pickup config
  if (agent.posProvider?.orderingModuleConfig?.pickupModuleConfig) {
    const pickupModuleConfig =
      agent.posProvider.orderingModuleConfig.pickupModuleConfig;
    result.pickupEnabled = pickupModuleConfig.enabled;
    result.pickupPaymentModuleConfig = pickupModuleConfig.paymentModuleConfig;
  }

  // delivery config
  if (agent.posProvider?.orderingModuleConfig?.deliveryModuleConfig) {
    const deliveryModuleConfig =
      agent.posProvider.orderingModuleConfig.deliveryModuleConfig;
    result.deliveryEnabled = deliveryModuleConfig.enabled;
    result.deliveryVerifyAddress = deliveryModuleConfig.verifyAddress;
    result.deliveryLatitude = deliveryModuleConfig.latitude;
    result.deliveryLongitude = deliveryModuleConfig.longitude;
    result.deliveryFee = deliveryModuleConfig.deliveryFee;
    result.deliveryRadius = deliveryModuleConfig.deliveryRadius;
    result.deliveryRegionCode = deliveryModuleConfig.regionCode;
    result.deliveryWaiveMinimum = deliveryModuleConfig.waiveDeliveryMinimum;
    result.deliveryPaymentModuleConfig =
      deliveryModuleConfig.paymentModuleConfig;
  }

  if (agent.reservationProvider) {
    result.reservationProvider = agent.reservationProvider.provider;
    result.reservationAccessToken = agent.reservationProvider.accessToken;
    result.reservationUrl = agent.reservationProvider.url;
    result.reservationEnabled =
      agent.reservationProvider.reservationModuleConfig?.enabled;
    result.reservationSlotDuration =
      agent.reservationProvider.reservationModuleConfig?.slotDuration;
    result.reservationMaxPartySize =
      agent.reservationProvider.reservationModuleConfig?.maxPartySize;
  }

  if (agent.voicemailConfig) {
    result.voicemailEmail = agent.voicemailConfig?.email;
  }

  if (agent.streamConnectedAt) {
    result.streamConnectedAt = agent.streamConnectedAt;
  }

  return result;
};

/**
 * Converts IAgentBlueprint to CreateAgentFormValues
 */
export const convertAgentBlueprintToCreateFormValues = (
  agent: IAgentBlueprint,
): CreateAgentFormValues => {
  return {
    ...convertAgentBlueprintToBaseFormValues(agent),
    voiceId: null,
  };
};

/**
 * Converts IAgentBlueprint to PatchAgentFormValues
 */
export const convertAgentBlueprintToFormValues = (
  agent: IAgentBlueprint,
): PatchAgentFormValues => {
  return {
    ...convertAgentBlueprintToBaseFormValues(agent),
    slug: agent.slug,
    phoneNumber: agent.phoneNumber,
    hoursLoman: agent.hoursLoman,
    disallowClosedTransfers: agent.disallowClosedTransfers,
    faqs: agent.faqs || [],
    allowAnon: agent.allowAnon,
  };
};

/**
 * Converts CreateAgentFormValues to IAgentBlueprint
 */
export const convertCreateAgentFormValuesToAgentBlueprint = (
  formValues: CreateAgentFormValues | PatchAgentFormValues,
): IAgentBlueprint => {
  return convertFormValuesToAgentBlueprint(formValues, false);
};

/**
 * Converts PatchAgentFormValues to IAgentBlueprint
 */
export const convertPatchAgentFormValuesToAgentBlueprint = (
  formValues: PatchAgentFormValues,
): IAgentBlueprint => {
  return convertFormValuesToAgentBlueprint(formValues, true);
};

const convertFormValuesToAgentBlueprint = (
  formValues: CreateAgentFormValues | PatchAgentFormValues,
  isPatch: boolean = false,
): IAgentBlueprint => {
  const blueprint: IAgentBlueprint = {
    id: formValues.id,
    accountId: formValues.accountId,
    fallbackPhoneNumber: formValues.fallbackPhoneNumber,
    greetingMessageOpen: formValues.greetingMessageOpen,
    greetingMessageOrderingClosed: formValues.greetingMessageOrderingClosed,
    greetingMessageClosed: formValues.greetingMessageClosed,
    hours: formValues.hours,
    hoursOrdering: formValues.hoursOrdering,
    messages: formValues.messages,
    name: formValues.name,
    placeId: formValues.placeId,
    pos: formValues.pos,
    posProvider: null,
    prompt: formValues.prompt,
    smsPhoneNumber: formValues.smsPhoneNumber,
    timezone: formValues.timezone,
    transfers: formValues.transfers,
    twilioOverride: formValues.twilioOverride?.enabled
      ? {
          enabled: formValues.twilioOverride.enabled,
          sid: formValues.twilioOverride.sid,
          auth: formValues.twilioOverride.auth,
        }
      : undefined,
    twoPartyConsent: formValues.twoPartyConsent,
    type: formValues.type,
    // Convert numeric prep times to string for backend
    pickupPrepTime:
      formValues.pickupPrepTime !== null &&
      formValues.pickupPrepTime !== undefined
        ? String(formValues.pickupPrepTime)
        : null,
    deliveryPrepTime:
      formValues.deliveryPrepTime !== null &&
      formValues.deliveryPrepTime !== undefined
        ? String(formValues.deliveryPrepTime)
        : null,
    customPrepTimeEnabled: formValues.customPrepTimeEnabled,
    customPrepTimeHours: formValues.customPrepTimeHours,
  };

  if (isPatch) {
    const patchFormValues = formValues as PatchAgentFormValues;
    blueprint.slug = patchFormValues.slug;
    blueprint.phoneNumber = patchFormValues.phoneNumber;
    blueprint.hoursLoman = patchFormValues.hoursLoman;
    blueprint.disallowClosedTransfers = patchFormValues.disallowClosedTransfers;
    blueprint.faqs = patchFormValues.faqs;
    blueprint.allowAnon = patchFormValues.allowAnon;
  }

  // pos provider
  if (formValues.pos) {
    const basePosProvider = {
      provider: formValues.pos,
      menuModuleConfig: formValues.menuModuleConfig
        ? convertFormValuesToMenuConfig(
            formValues.menuModuleConfig as MenuConfigFormValues,
          )
        : {
            enabled: true,
          },
      orderingModuleConfig: {
        orderUntilClosed: formValues.orderingOrderUntilClosed || false,
        allowScheduledOrders:
          formValues.orderingAllowScheduledOrders || "never",
        shouldTotal: formValues.orderingShouldTotal || false,
        exactTimeEstimate: formValues.orderingExactTimeEstimate || false,
        instantSubmit: formValues.orderingInstantSubmit || false,
        minLeadTime: formValues.orderingMinLeadTime,
        minTotalThreshold: formValues.orderingMinTotalThreshold,
        maxTotalThreshold: formValues.orderingMaxTotalThreshold,
        pickupModuleConfig: {
          enabled: formValues.pickupEnabled || false,
          paymentModuleConfig: formValues.pickupPaymentModuleConfig ?? {
            provider: "none",
          },
        },
        deliveryModuleConfig: {
          enabled: formValues.deliveryEnabled || false,
          paymentModuleConfig: formValues.deliveryPaymentModuleConfig ?? {
            provider: "none",
          },
          verifyAddress: formValues.deliveryVerifyAddress || false,
          latitude: formValues.deliveryLatitude || 0,
          longitude: formValues.deliveryLongitude || 0,
          deliveryFee: formValues.deliveryFee || 0,
          deliveryRadius: formValues.deliveryRadius || 0,
          regionCode: formValues.deliveryRegionCode || "US",
          waiveDeliveryMinimum: formValues.deliveryWaiveMinimum,
        },
      },
    };

    // pos specific fields
    switch (formValues.pos) {
      case "toast":
        blueprint.posProvider = {
          ...basePosProvider,
          url: formValues.toastUrl || "",
          orderingModuleConfig: basePosProvider.orderingModuleConfig
            ? {
                ...basePosProvider.orderingModuleConfig,
                taxRate: formValues.orderingTaxRate || 0,
                dynamicPrepTime: formValues.orderingDynamicPrepTime || false,
              }
            : undefined,
        } as IPosProviderToast;
        break;
      case "square":
        blueprint.posProvider = {
          ...basePosProvider,
          merchantId: formValues.squareMerchantId || "",
          locationId: formValues.squareLocationId || "",
          accessToken: formValues.squareAccessToken || "",
          refreshToken: formValues.squareRefreshToken || "",
        } as IPosProviderSquare;
        break;
      case "clover":
        blueprint.posProvider = {
          ...basePosProvider,
          merchantId: formValues.cloverMerchantId || "",
          accessToken: formValues.cloverAccessToken || "",
          refreshToken: formValues.cloverRefreshToken || "",
          refreshTokenExpiration: formValues.cloverRefreshTokenExpiration || 0,
          orderTypeId: formValues.cloverOrderTypeId || "",
        } as IPosProviderClover;
        break;
      case "stream":
        blueprint.posProvider = {
          ...basePosProvider,
          subprovider: formValues.subprovider,
          locationId: formValues.locationId,
          orderingModuleConfig: basePosProvider.orderingModuleConfig
            ? {
                ...basePosProvider.orderingModuleConfig,
                taxRate: formValues.orderingTaxRate || 0,
                useOrderEntryTool: formValues.useOrderEntryTool,
              }
            : undefined,
        } as IPosProviderStream;
        break;
      case "kwick":
        blueprint.posProvider = {
          ...basePosProvider,
          merchantKey: formValues.kwickMerchantKey || "",
          merchantToken: formValues.kwickMerchantToken || "",
        } as IPosProviderKwick;
        break;
      case "loman":
        blueprint.posProvider = {
          ...basePosProvider,
          orderingModuleConfig: basePosProvider.orderingModuleConfig
            ? {
                ...basePosProvider.orderingModuleConfig,
                taxRate: formValues.orderingTaxRate || 0,
              }
            : undefined,
        } as IPosProviderLoman;
        break;
      case "spotOn":
        blueprint.posProvider = {
          ...basePosProvider,
          locationId: formValues.spotOnLocationId || "",
        } as IPosProviderSpotOn;
        break;
      case "foodtec":
        blueprint.posProvider = {
          ...basePosProvider,
          baseUrl: formValues.foodtecBaseUrl || "",
          menuAuth: formValues.foodtecMenuAuth || "",
          checkoutAuth: formValues.foodtecCheckoutAuth || "",
          orderAuth: formValues.foodtecOrderAuth || "",
          orderTypes: formValues.foodtecOrderTypes || [],
          orderingModuleConfig: basePosProvider.orderingModuleConfig
            ? {
                ...basePosProvider.orderingModuleConfig,
                useOrderEntryTool: formValues.useOrderEntryTool,
              }
            : undefined,
        } as IPosProviderFoodtec;
        break;
    }
  }

  if (formValues.reservationEnabled === true) {
    blueprint.reservationProvider = {
      provider: formValues.reservationProvider || "resos",
      accessToken: formValues.reservationAccessToken || "",
      url: formValues.reservationUrl || "",
      reservationModuleConfig: {
        enabled: formValues.reservationEnabled,
        slotDuration: formValues.reservationSlotDuration || 0,
        maxPartySize: formValues.reservationMaxPartySize || 0,
      },
    };
  }

  if (formValues.voicemailEmail) {
    blueprint.voicemailConfig = {
      email: formValues.voicemailEmail,
    };
  }

  return blueprint;
};

/**
 * Converts nested menu config to flattened structure for easier dirty field tracking
 */
export const convertMenuConfigToFormValues = (
  menuConfig: IMenuModuleConfig,
): MenuConfigFormValues => {
  const formValues: MenuConfigFormValues = {
    menuEnabled: menuConfig.enabled || false,
    menuLocked: menuConfig.locked ?? false,
    manualMenu: menuConfig.manualMenu,
    menuReplaceMap: menuConfig.replaceMap,
  };
  if (menuConfig.categoryBlacklist) {
    Object.entries(menuConfig.categoryBlacklist).forEach(
      ([categoryId, value]) => {
        formValues[`categoryBlacklist.${categoryId}`] = value;
      },
    );
  }
  if (menuConfig.categoryNameOverrides) {
    Object.entries(menuConfig.categoryNameOverrides).forEach(
      ([categoryId, value]) => {
        formValues[`categoryNameOverrides.${categoryId}`] = value;
      },
    );
  }
  if (menuConfig.categoryDescriptionOverrides) {
    Object.entries(menuConfig.categoryDescriptionOverrides).forEach(
      ([categoryId, value]) => {
        formValues[`categoryDescriptionOverrides.${categoryId}`] = value;
      },
    );
  }
  if (menuConfig.categoryAvailabilityOverrides) {
    Object.entries(menuConfig.categoryAvailabilityOverrides).forEach(
      ([categoryId, value]) => {
        formValues[`categoryAvailabilityOverrides.${categoryId}`] = value;
      },
    );
  }
  if (menuConfig.categoryPrepTimeOverrides) {
    Object.entries(menuConfig.categoryPrepTimeOverrides).forEach(
      ([categoryId, value]) => {
        formValues[`categoryPrepTimeOverrides.${categoryId}`] = value;
      },
    );
  }
  if (menuConfig.itemBlacklist) {
    Object.entries(menuConfig.itemBlacklist).forEach(([itemId, value]) => {
      formValues[`itemBlacklist.${itemId}`] = value;
    });
  }
  if (menuConfig.itemNameOverrides) {
    Object.entries(menuConfig.itemNameOverrides).forEach(([itemId, value]) => {
      formValues[`itemNameOverrides.${itemId}`] = value;
    });
  }
  if (menuConfig.itemDescriptionOverrides) {
    Object.entries(menuConfig.itemDescriptionOverrides).forEach(
      ([itemId, value]) => {
        formValues[`itemDescriptionOverrides.${itemId}`] = value;
      },
    );
  }
  if (menuConfig.itemAvailabilityOverrides) {
    Object.entries(menuConfig.itemAvailabilityOverrides).forEach(
      ([itemId, value]) => {
        formValues[`itemAvailabilityOverrides.${itemId}`] = value;
      },
    );
  }
  if (menuConfig.itemPrepTimeOverrides) {
    Object.entries(menuConfig.itemPrepTimeOverrides).forEach(
      ([itemId, value]) => {
        formValues[`itemPrepTimeOverrides.${itemId}`] = value;
      },
    );
  }
  if (menuConfig.notesMap) {
    Object.entries(menuConfig.notesMap).forEach(([itemId, value]) => {
      formValues[`notesMap.${itemId}`] = value;
    });
  }
  if (menuConfig.modifierBlacklist) {
    Object.entries(menuConfig.modifierBlacklist).forEach(
      ([modifierId, value]) => {
        formValues[`modifierBlacklist.${modifierId}`] = value;
      },
    );
  }
  if (menuConfig.modifierNameOverrides) {
    Object.entries(menuConfig.modifierNameOverrides).forEach(
      ([modifierId, value]) => {
        formValues[`modifierNameOverrides.${modifierId}`] = value;
      },
    );
  }
  if (menuConfig.forceRequired) {
    Object.entries(menuConfig.forceRequired).forEach(([modifierId, value]) => {
      formValues[`forceRequired.${modifierId}`] = value;
    });
  }
  if (menuConfig.freeModifierCount) {
    Object.entries(menuConfig.freeModifierCount).forEach(
      ([modifierId, value]) => {
        formValues[`freeModifierCount.${modifierId}`] = value;
      },
    );
  }
  if (menuConfig.allowStackedOverrides) {
    Object.entries(menuConfig.allowStackedOverrides).forEach(
      ([modifierId, value]) => {
        formValues[`allowStackedOverrides.${modifierId}`] = value;
      },
    );
  }
  if (menuConfig.optionBlacklist) {
    Object.entries(menuConfig.optionBlacklist).forEach(([optionId, value]) => {
      formValues[`optionBlacklist.${optionId}`] = value;
    });
  }
  if (menuConfig.optionNameOverrides) {
    Object.entries(menuConfig.optionNameOverrides).forEach(
      ([optionId, value]) => {
        formValues[`optionNameOverrides.${optionId}`] = value;
      },
    );
  }
  if (menuConfig.defaultOptionsOverrides) {
    Object.entries(menuConfig.defaultOptionsOverrides).forEach(
      ([itemId, optionMap]) => {
        Object.entries(optionMap).forEach(([optionId, value]) => {
          formValues[`defaultOptionsOverrides.${itemId}.${optionId}`] = value;
        });
      },
    );
  }
  return formValues;
};

/**
 * Converts flattened menu config back to nested structure for API calls
 */
export const convertFormValuesToMenuConfig = (
  menuFormValues: MenuConfigFormValues,
): IMenuModuleConfig => {
  const normalizedConfig = { ...menuFormValues };
  if (
    menuFormValues.categoryNameOverrides &&
    typeof menuFormValues.categoryNameOverrides === "object"
  ) {
    Object.entries(menuFormValues.categoryNameOverrides).forEach(
      ([id, value]) => {
        normalizedConfig[`categoryNameOverrides.${id}`] = value;
      },
    );
    delete normalizedConfig.categoryNameOverrides;
  }
  if (
    menuFormValues.categoryBlacklist &&
    typeof menuFormValues.categoryBlacklist === "object"
  ) {
    Object.entries(menuFormValues.categoryBlacklist).forEach(([id, value]) => {
      normalizedConfig[`categoryBlacklist.${id}`] = value;
    });
    delete normalizedConfig.categoryBlacklist;
  }
  if (
    menuFormValues.categoryDescriptionOverrides &&
    typeof menuFormValues.categoryDescriptionOverrides === "object"
  ) {
    Object.entries(menuFormValues.categoryDescriptionOverrides).forEach(
      ([id, value]) => {
        normalizedConfig[`categoryDescriptionOverrides.${id}`] = value;
      },
    );
    delete normalizedConfig.categoryDescriptionOverrides;
  }
  if (
    menuFormValues.categoryAvailabilityOverrides &&
    typeof menuFormValues.categoryAvailabilityOverrides === "object"
  ) {
    Object.entries(menuFormValues.categoryAvailabilityOverrides).forEach(
      ([id, value]) => {
        normalizedConfig[`categoryAvailabilityOverrides.${id}`] = value;
      },
    );
    delete normalizedConfig.categoryAvailabilityOverrides;
  }
  if (
    menuFormValues.categoryPrepTimeOverrides &&
    typeof menuFormValues.categoryPrepTimeOverrides === "object"
  ) {
    Object.entries(menuFormValues.categoryPrepTimeOverrides).forEach(
      ([id, value]) => {
        normalizedConfig[`categoryPrepTimeOverrides.${id}`] = value;
      },
    );
    delete normalizedConfig.categoryPrepTimeOverrides;
  }
  if (
    menuFormValues.itemBlacklist &&
    typeof menuFormValues.itemBlacklist === "object"
  ) {
    Object.entries(menuFormValues.itemBlacklist).forEach(([id, value]) => {
      normalizedConfig[`itemBlacklist.${id}`] = value;
    });
    delete normalizedConfig.itemBlacklist;
  }
  if (
    menuFormValues.itemNameOverrides &&
    typeof menuFormValues.itemNameOverrides === "object"
  ) {
    Object.entries(menuFormValues.itemNameOverrides).forEach(([id, value]) => {
      normalizedConfig[`itemNameOverrides.${id}`] = value;
    });
    delete normalizedConfig.itemNameOverrides;
  }
  if (
    menuFormValues.itemDescriptionOverrides &&
    typeof menuFormValues.itemDescriptionOverrides === "object"
  ) {
    Object.entries(menuFormValues.itemDescriptionOverrides).forEach(
      ([id, value]) => {
        normalizedConfig[`itemDescriptionOverrides.${id}`] = value;
      },
    );
    delete normalizedConfig.itemDescriptionOverrides;
  }
  if (
    menuFormValues.itemAvailabilityOverrides &&
    typeof menuFormValues.itemAvailabilityOverrides === "object"
  ) {
    Object.entries(menuFormValues.itemAvailabilityOverrides).forEach(
      ([id, value]) => {
        normalizedConfig[`itemAvailabilityOverrides.${id}`] = value;
      },
    );
    delete normalizedConfig.itemAvailabilityOverrides;
  }
  if (
    menuFormValues.itemPrepTimeOverrides &&
    typeof menuFormValues.itemPrepTimeOverrides === "object"
  ) {
    Object.entries(menuFormValues.itemPrepTimeOverrides).forEach(
      ([id, value]) => {
        normalizedConfig[`itemPrepTimeOverrides.${id}`] = value;
      },
    );
    delete normalizedConfig.itemPrepTimeOverrides;
  }
  if (menuFormValues.notesMap && typeof menuFormValues.notesMap === "object") {
    Object.entries(menuFormValues.notesMap).forEach(([id, value]) => {
      normalizedConfig[`notesMap.${id}`] = value;
    });
    delete normalizedConfig.notesMap;
  }

  if (
    menuFormValues.modifierBlacklist &&
    typeof menuFormValues.modifierBlacklist === "object"
  ) {
    Object.entries(menuFormValues.modifierBlacklist).forEach(([id, value]) => {
      normalizedConfig[`modifierBlacklist.${id}`] = value;
    });
    delete normalizedConfig.modifierBlacklist;
  }

  if (
    menuFormValues.modifierNameOverrides &&
    typeof menuFormValues.modifierNameOverrides === "object"
  ) {
    Object.entries(menuFormValues.modifierNameOverrides).forEach(
      ([id, value]) => {
        normalizedConfig[`modifierNameOverrides.${id}`] = value;
      },
    );
    delete normalizedConfig.modifierNameOverrides;
  }

  if (
    menuFormValues.forceRequired &&
    typeof menuFormValues.forceRequired === "object"
  ) {
    Object.entries(menuFormValues.forceRequired).forEach(([id, value]) => {
      normalizedConfig[`forceRequired.${id}`] = value;
    });
    delete normalizedConfig.forceRequired;
  }

  if (
    menuFormValues.freeModifierCount &&
    typeof menuFormValues.freeModifierCount === "object"
  ) {
    Object.entries(menuFormValues.freeModifierCount).forEach(([id, value]) => {
      normalizedConfig[`freeModifierCount.${id}`] = value;
    });
    delete normalizedConfig.freeModifierCount;
  }

  if (
    menuFormValues.allowStackedOverrides &&
    typeof menuFormValues.allowStackedOverrides === "object"
  ) {
    Object.entries(menuFormValues.allowStackedOverrides).forEach(
      ([id, value]) => {
        normalizedConfig[`allowStackedOverrides.${id}`] = value;
      },
    );
    delete normalizedConfig.allowStackedOverrides;
  }

  if (
    menuFormValues.optionBlacklist &&
    typeof menuFormValues.optionBlacklist === "object"
  ) {
    Object.entries(menuFormValues.optionBlacklist).forEach(([id, value]) => {
      normalizedConfig[`optionBlacklist.${id}`] = value;
    });
    delete normalizedConfig.optionBlacklist;
  }

  if (
    menuFormValues.optionNameOverrides &&
    typeof menuFormValues.optionNameOverrides === "object"
  ) {
    Object.entries(menuFormValues.optionNameOverrides).forEach(
      ([id, value]) => {
        normalizedConfig[`optionNameOverrides.${id}`] = value;
      },
    );
    delete normalizedConfig.optionNameOverrides;
  }

  if (
    menuFormValues.defaultOptionsOverrides &&
    typeof menuFormValues.defaultOptionsOverrides === "object"
  ) {
    Object.entries(menuFormValues.defaultOptionsOverrides).forEach(
      ([itemId, optionMap]) => {
        if (typeof optionMap === "object" && optionMap !== null) {
          Object.entries(optionMap).forEach(([optionId, value]) => {
            normalizedConfig[`defaultOptionsOverrides.${itemId}.${optionId}`] =
              value;
          });
        }
      },
    );
    delete normalizedConfig.defaultOptionsOverrides;
  }
  const nested: IMenuModuleConfig & {
    [key: string]: unknown;
  } = {
    enabled: normalizedConfig.menuEnabled || false,
    locked: normalizedConfig.menuLocked ?? false,
    manualMenu: normalizedConfig.manualMenu as Menu | undefined,
    replaceMap: normalizedConfig.menuReplaceMap,
  };

  const overrides: Record<string, Record<string, unknown>> = {
    categoryBlacklist: {},
    categoryNameOverrides: {},
    categoryDescriptionOverrides: {},
    categoryAvailabilityOverrides: {},
    categoryPrepTimeOverrides: {},
    itemBlacklist: {},
    itemNameOverrides: {},
    itemDescriptionOverrides: {},
    itemAvailabilityOverrides: {},
    itemPrepTimeOverrides: {},
    notesMap: {},
    modifierBlacklist: {},
    modifierNameOverrides: {},
    forceRequired: {},
    freeModifierCount: {},
    allowStackedOverrides: {},
    optionBlacklist: {},
    optionNameOverrides: {},
  };

  // Single pass through all flattened fields
  Object.entries(normalizedConfig).forEach(([key, value]) => {
    if (value === undefined) return;

    // Handle defaultOptionsOverrides (special case with nested structure)
    if (key.startsWith("defaultOptionsOverrides.")) {
      const parts = key.substring("defaultOptionsOverrides.".length).split(".");
      if (parts.length === 2) {
        const [itemId, optionId] = parts;
        if (!nested.defaultOptionsOverrides) {
          nested.defaultOptionsOverrides = {};
        }
        const defaultOptionsOverrides =
          nested.defaultOptionsOverrides as Record<
            string,
            Record<string, unknown>
          >;
        if (!defaultOptionsOverrides[itemId]) {
          defaultOptionsOverrides[itemId] = {};
        }
        defaultOptionsOverrides[itemId][optionId] = value;
      }
      return;
    }

    // Handle all other override types
    for (const [overrideType, overrideObj] of Object.entries(overrides)) {
      if (key.startsWith(`${overrideType}.`)) {
        const id = key.substring(overrideType.length + 1);
        overrideObj[id] = value;
        break;
      }
    }
  });

  // Only add non-empty override objects to the nested structure
  Object.entries(overrides).forEach(([overrideType, overrideObj]) => {
    if (Object.keys(overrideObj).length > 0) {
      nested[overrideType] = overrideObj;
    }
  });

  return nested;
};
